import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';

import { DateTime } from 'luxon';

import { NavLinkButton, Col, Row } from '@shared/components/bootstrap';

import { Preview as AddressPreview } from '@admin/components/address/preview';
import { Panel } from '@admin/components/helpers/panel';
import { Permission } from '@admin/components/helpers/permission';
import { OrderKindDescription } from '@admin/components/order_summary/order_kind_description';
import { FormattedServiceType } from '@admin/components/orders/formatted_service_type';
import { Formatter as PhoneFormatter } from '@admin/components/phone/formatter';
import { Summary as SubscriptionsSummary } from '@admin/components/subscriptions/summary';
import { dispatchesURL, dispatcherURL, orderURL } from '@admin/config/routes';
import { ADMIN_DISPATCH } from '@admin/libraries/access_control_catalog.generated';
import { DispatchShowQuery, Assignment__Role } from '@admin/schema';

import { DispatchMap } from './map';
import { getCustomerNames, getLastOrderEvent, isWarehouseOrder } from './utils';

type Dispatch = DispatchShowQuery['dispatch'];
type Order = Dispatch['orders'][0];

const editDispatchURL = ({ id }: { id: string }) => `/dispatches/${id}/edit`;

const AssignmentRoleLabel: React.FC<{ role: Assignment__Role }> = ({ role }) => {
  const miscClass = (state: string) => {
    switch (state.toLowerCase()) {
      case 'lead':
        return 'label-primary';
      case 'accepted':
        return 'label-success';
      case 'rejected':
        return 'label-warning';
      case 'canceled':
        return 'label-danger';
      case 'completed':
        return 'label-info';
      default:
        return 'label-default';
    }
  };

  const label = role
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
  return <span className={`label ${miscClass(role)}`}>{label}</span>;
};

const CollapsiblePanel: React.FC<{ title: string; children: React.ReactNode; footer?: React.ReactNode }> = ({
  title,
  children,
  footer,
}) => {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <Panel>
      <Panel.Header>
        <Panel.Collapse collapsed={collapsed} onChange={setCollapsed} />
        <Panel.Title>{title}</Panel.Title>
      </Panel.Header>
      {!collapsed && (
        <>
          <Panel.Body>{children}</Panel.Body>
          {footer && <Panel.Footer align="right">{footer}</Panel.Footer>}
        </>
      )}
    </Panel>
  );
};

const OrderAddressCell: React.FC<{ order: Order }> = ({ order }) => {
  if (order.movingOperation) {
    return (
      <>
        <p>
          <strong>Pickup:</strong>{' '}
          <a href={order.movingOperation.originAddress.map} target="_blank" rel="noreferrer">
            <AddressPreview address={order.movingOperation.originAddress} mode="inline" />
          </a>
        </p>
        <p>
          <strong>Delivery:</strong>{' '}
          <a href={order.movingOperation.destinationAddress.map} target="_blank" rel="noreferrer">
            <AddressPreview address={order.movingOperation.destinationAddress} mode="inline" />
          </a>
        </p>
      </>
    );
  }

  return (
    <a href={order.address.map} target="_blank" rel="noreferrer">
      <AddressPreview address={order.address} />
    </a>
  );
};

export const DispatchShowDetails: React.FC<{ dispatch: Dispatch }> = ({ dispatch }) => {
  const { region } = dispatch;
  const arrivalDate = DateTime.fromISO(dispatch.arrival, { zone: region.tz });
  const dispatcherRegionID = region.parentID ?? region.id;
  const hasWarehouseOrders = dispatch.orders.some(isWarehouseOrder);

  return (
    <>
      <CollapsiblePanel
        title="Details"
        footer={
          <>
            <Permission resource={ADMIN_DISPATCH.resource} action={ADMIN_DISPATCH.actions.update} denied={null}>
              <NavLinkButton kind="primary" to={editDispatchURL({ id: dispatch.id })} className="mar-rgt">
                Edit
              </NavLinkButton>
            </Permission>
            <Permission resource={ADMIN_DISPATCH.resource} action={ADMIN_DISPATCH.actions.read} denied={null}>
              <NavLinkButton
                kind="primary"
                to={dispatcherURL({ regionID: dispatcherRegionID, date: arrivalDate.toFormat('yyyy-MM-dd') })}
              >
                View in Dispatcher
              </NavLinkButton>
            </Permission>
          </>
        }
      >
        <Row>
          <Col xs={12} md={4}>
            <ul className="list-unstyled">
              <li>
                <strong>ID: </strong>
                {dispatch.id}
              </li>
              <li>
                <strong>Date: </strong>
                {DateTime.fromISO(dispatch.createdAt, { zone: region.tz }).toFormat('MM/dd/yyyy')}
              </li>
              <li>
                <strong>Arrival: </strong>
                {arrivalDate.toFormat('MM/dd/yyyy, h:mm a')}
              </li>
              <li>
                <strong>Phone Number: </strong>
                {dispatch.phone ? <PhoneFormatter number={dispatch.phone} /> : null}
              </li>
              <li>
                <strong>Region: </strong>
                {region.name}
              </li>
              <li>
                <strong>Warehouse: </strong>
                {dispatch.warehouse.name}
              </li>
              <li>
                <strong>Inbound Warehouse: </strong>
                {dispatch.inboundWarehouse?.name}
              </li>
              <li>
                <strong>Depot: </strong>
                {dispatch.depot.name}
              </li>
              <li>
                <strong>Load Out: </strong>
                {dispatch.loadOut}
              </li>
              <li>
                <strong>Addons: </strong>
                {dispatch.addons.map((addon) => (
                  <span key={addon} className="label label-default mar-rgt">
                    {addon}
                  </span>
                ))}
              </li>
              <li>
                <strong>Mover Notes: </strong>
                {dispatch.moverNotes}
              </li>
              <li>
                <strong>Warehouse Notes: </strong>
                {dispatch.warehouseNotes}
              </li>
            </ul>
          </Col>
          {!hasWarehouseOrders && (
            <Col xs={12} md={8} className="fullscreen">
              <DispatchMap
                dispatchID={dispatch.id}
                arrival={dispatch.arrival}
                orders={dispatch.orders}
                vehicles={dispatch.vehicles}
                assignments={dispatch.assignments}
              />
            </Col>
          )}
        </Row>
      </CollapsiblePanel>

      <CollapsiblePanel title="Jobs">
        <table className="table table-bordered table-striped table-hover">
          <thead>
            <tr className="active">
              <th>ID</th>
              <th>Customer</th>
              <th>Plan</th>
              <th>Type</th>
              <th>Service Type</th>
              <th>Movers</th>
              <th>Address</th>
              <th>Scheduled</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {dispatch.orders.map((order) => (
              <tr key={order.id}>
                <td>{order.id}</td>
                <td>{getCustomerNames(order.account.customers)}</td>
                <td>
                  <SubscriptionsSummary
                    subscriptions={order.account.subscriptions.map((subscription) => ({
                      ...subscription,
                      display_quantity: subscription.quantity,
                    }))}
                    showCost={false}
                  />
                </td>
                <td>
                  <OrderKindDescription order={order} />
                </td>
                <td>
                  <FormattedServiceType order={order} />
                </td>
                <td>{order.movers}</td>
                <td>
                  <OrderAddressCell order={order} />
                </td>
                <td>{DateTime.fromISO(order.scheduled, { zone: region.tz }).toFormat('h:mma')}</td>
                <td>{getLastOrderEvent(order.orderEventsExcludingNonLeadAssignments)}</td>
                <td>
                  <NavLinkButton
                    kind="primary"
                    size="sm"
                    to={orderURL({ id: order.id, account: order.account })}
                    target="_blank"
                  >
                    View
                  </NavLinkButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CollapsiblePanel>

      <CollapsiblePanel title="Team">
        <div className="table-responsive">
          <table className="table table-striped">
            <thead>
              <tr>
                <th className="text-left col-sm-2">Role</th>
                <th className="text-left col-sm-4">Name</th>
                <th className="text-left col-sm-2">Email</th>
                <th className="text-left col-sm-2">Title</th>
              </tr>
            </thead>
            <tbody>
              {dispatch.assignments.map((assignment) => (
                <tr key={assignment.id}>
                  <td className="text-left col-sm-2">
                    <AssignmentRoleLabel role={assignment.role} />
                  </td>
                  <td className="text-left col-sm-2">{assignment.user.name}</td>
                  <td className="text-left col-sm-2">
                    <a href={`mailto:${assignment.user.email}`}>{assignment.user.email}</a>
                  </td>
                  <td className="text-left col-sm-2">{assignment.user.hrsTitle}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CollapsiblePanel>
    </>
  );
};

const Breadcrumbs: React.FC<{ dispatch: Dispatch }> = ({ dispatch }) => {
  const { region, arrival, id } = dispatch;
  const date = DateTime.fromISO(arrival, { zone: region.tz });

  return (
    <h1 className="page-header text-overflow">
      <ol className="breadcrumb">
        <li>
          <NavLink to={dispatchesURL({ region: region.id })}>{region.name}</NavLink>
        </li>
        <li>
          <NavLink to={dispatchesURL({ region: region.id, date: date.toFormat('yyyy-MM-dd') })}>
            {date.toFormat('MM/dd/yyyy')}
          </NavLink>
        </li>
        <li className="active">Dispatch #{id}</li>
      </ol>
    </h1>
  );
};

export { Breadcrumbs };
